import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { FireUIAnalytics, fireUIAnalytics } from '@atlassian/analytics-bridge';
import { AttachmentFieldError, AttachmentFieldForUploadingWrapper, AttachmentFieldView, transformReadAttachmentsToken } from '@atlassian/jsm-attachment';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { useMediaContext } from '../../../jira-common/context';
import { toAttachmentQuestionAnswer } from '../../stores/QuestionStore';
import { DisplayMode } from '../RenderForm';
export var Attachment = observer(function (_ref) {
  var id = _ref.id,
    errorId = _ref.errorId,
    formStore = _ref.formStore,
    questionStore = _ref.questionStore,
    blockFormSubmission = _ref.blockFormSubmission,
    displayMode = _ref.displayMode;
  var _useMediaContext = useMediaContext(),
    mediaApiUploadToken = _useMediaContext.mediaApiUploadToken,
    mediaApiReadToken = _useMediaContext.mediaApiReadToken,
    mediaUploadAndViewContexts = _useMediaContext.mediaUploadAndViewContexts,
    projectId = _useMediaContext.projectId,
    portalId = _useMediaContext.portalId,
    issueKey = _useMediaContext.issueKey,
    mediaApiError = _useMediaContext.mediaApiError;
  var fieldId = questionStore.id;
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;

  // todo: use the function transformUploadContext from @atlassian/jsm-attachment instead
  var mediaTokenToContext = function mediaTokenToContext(mediaApiUploadToken) {
    return {
      clientId: mediaApiUploadToken.clientId,
      serviceHost: mediaApiUploadToken.endpointUrl,
      token: mediaApiUploadToken.token,
      collection: mediaApiUploadToken.targetCollection,
      tokenLifespanInMs: mediaApiUploadToken.tokenDurationInMins * 60 * 1000,
      tokenIssueTimestamp: Date.now() - 10000
    };
  };
  var mediaContext = useMemo(function () {
    if (mediaUploadAndViewContexts) {
      // issue-view page
      return mediaUploadAndViewContexts;
    }
    // every other page
    return {
      uploadContext: mediaApiUploadToken ? mediaTokenToContext(mediaApiUploadToken) : null,
      viewContext: mediaApiReadToken ? transformReadAttachmentsToken(mediaApiReadToken) : null
    };
  }, [mediaApiReadToken, mediaApiUploadToken, mediaUploadAndViewContexts]);

  // Handles updating the question's answers when the attachments change
  var handleAttachmentUpdate = useCallback(function (mediaFiles) {
    questionStore.setAnswer(mediaFiles);
  }, [questionStore]);
  var initialFiles = (toAttachmentQuestionAnswer(questionStore.currentAnswer) || []).map(function (f) {
    var _f$name, _f$size, _f$creationDate, _f$type;
    return {
      id: f.id,
      name: (_f$name = f.name) !== null && _f$name !== void 0 ? _f$name : '',
      size: (_f$size = f.size) !== null && _f$size !== void 0 ? _f$size : 0,
      creationDate: (_f$creationDate = f.creationDate) !== null && _f$creationDate !== void 0 ? _f$creationDate : 0,
      type: (_f$type = f.type) !== null && _f$type !== void 0 ? _f$type : 'file'
    };
  });
  var shouldResetAttachment = useMemo(function () {
    var attachmentAnswer = questionStore.answer;
    var modifiedAnswer = questionStore.modifiedAnswer;
    return (modifiedAnswer === undefined || (modifiedAnswer === null || modifiedAnswer === void 0 ? void 0 : modifiedAnswer.length) === 0) && (attachmentAnswer === null || attachmentAnswer === void 0 ? void 0 : attachmentAnswer.length) === 0;
  }, [questionStore.answer, questionStore.modifiedAnswer]);
  var isViewMode = displayMode === DisplayMode.View || displayMode === DisplayMode.Preview;
  if (isViewMode) {
    if (!mediaContext.viewContext) {
      fireUIAnalytics(createAnalyticsEvent({
        actionSubject: ActionSubject.ATTACHMENT,
        action: Action.FAILED
      }), "".concat(ActionSubject.ATTACHMENT, " ").concat(Action.FAILED), projectId ? {
        projectId: projectId,
        issueKey: issueKey,
        mediaApiError: mediaApiError,
        isViewMode: true
      } : {
        portalId: portalId,
        issueKey: issueKey,
        mediaApiError: mediaApiError,
        isViewMode: true
      });
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AttachmentFieldView, {
      mediaViewContext: mediaContext.viewContext,
      mediaApiError: mediaApiError,
      files: initialFiles
    }));
  }

  // upload pages will only have upload tokens
  if (mediaContext.uploadContext) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AttachmentFieldForUploadingWrapper, {
      setUploadingMedia: questionStore.setUploadingMedia,
      mediaUploadAndViewContexts: mediaContext,
      projectId: projectId ? projectId.toString() : undefined,
      portalId: portalId ? portalId.toString() : undefined,
      fieldId: fieldId ? fieldId.toString() : undefined,
      onAttachmentUpdate: handleAttachmentUpdate,
      initialFiles: initialFiles,
      blockFormSubmission: blockFormSubmission,
      shouldResetAttachments: shouldResetAttachment,
      numberOfAttachmentsOnForm: formStore.numberOfAttachment
    }), /*#__PURE__*/React.createElement(FireUIAnalytics, {
      actionSubject: ActionSubject.ATTACHMENT_UPLOAD,
      action: Action.VIEWED,
      actionSubjectId: "proformaAttachmentUploadField"
    }));
  }
  fireUIAnalytics(createAnalyticsEvent({
    actionSubject: ActionSubject.ATTACHMENT,
    action: Action.FAILED
  }), "".concat(ActionSubject.ATTACHMENT, " ").concat(Action.FAILED), projectId ? {
    projectId: projectId,
    issueKey: issueKey,
    mediaApiError: mediaApiError,
    isViewMode: isViewMode
  } : {
    portalId: portalId,
    issueKey: issueKey,
    mediaApiError: mediaApiError,
    isViewMode: isViewMode
  });
  return /*#__PURE__*/React.createElement(AttachmentFieldError, {
    error: mediaApiError
  });
});